import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { userAPI } from '../services/api';
import GlobalDashboard from '../components/GlobalDashboard';

const AccountOverview = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [dashboardData, setDashboardData] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('overviewViewMode') || 'detailed';
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Load overview dashboard first (critical data for main UI)
        const overviewDashboard = await userAPI.getOverviewDashboard();
        setOverviewData(overviewDashboard);
        
        // Show UI immediately with overview data
        setLoading(false);
        
        // Load legacy dashboard data in background (for account stats section)
        userAPI.getDashboard()
          .then(regularDashboard => {
            setDashboardData(regularDashboard);
          })
          .catch(error => {
            console.warn('Failed to fetch legacy dashboard data:', error);
            // Don't show error since overview data loaded successfully
          });
        
      } catch (error) {
        console.error('Failed to fetch overview dashboard data:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const toggleViewMode = () => {
    const newMode = viewMode === 'detailed' ? 'simple' : 'detailed';
    setViewMode(newMode);
    localStorage.setItem('overviewViewMode', newMode);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] dashboard-optimized">
        <div className="glass-spinner w-12 h-12 mb-4"></div>
        <p className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8">
        <div className="glass-alert-error rounded-lg p-6 max-w-md w-full">
          <div className="text-red-300 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="heading-mono text-base text-red-300 mb-2 text-center">Failed to load dashboard</h3>
          <p className="text-mono text-sm text-red-200 text-center">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-scale">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`heading-mono text-2xl font-bold text-glow ${isDark ? 'text-white' : 'text-gray-800'}`}>
            Welcome back, {user?.firstName}
          </h1>
          <p className={`text-mono text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Here's an overview of your account and recent activity
          </p>
        </div>
        <button
          onClick={toggleViewMode}
          className="glass-tab px-4 py-2 rounded-lg text-mono text-sm flex items-center gap-2 transition-all bg-accent-400/20 border-accent-400 hover:bg-accent-400/30"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
          {viewMode === 'detailed' ? 'Detailed View' : 'Simple View'}
        </button>
      </div>

      {/* Global Analytics Dashboard */}
      <GlobalDashboard overviewData={overviewData} viewMode={viewMode} />

      {/* Account Information Card */}
      <div className="glass-card rounded-2xl p-8 dashboard-optimized">
        <h3 className={`heading-mono text-lg text-glow mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>
          Account Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className={`text-mono text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Email</p>
            <p className={`text-mono text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>{user?.email}</p>
          </div>
          <div>
            <p className={`text-mono text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Member Since</p>
            <p className={`text-mono text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>{formatDate(user?.createdAt)}</p>
          </div>
          <div>
            <p className={`text-mono text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Last Login</p>
            <p className={`text-mono text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>{formatDate(user?.lastLogin)}</p>
          </div>
          <div>
            <p className={`text-mono text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Account Status</p>
            <span className={`glass-badge inline-flex px-3 py-1 text-xs font-medium rounded-full ${
              user?.isActive 
                ? 'glass-badge-success' 
                : 'glass-badge-error'
            }`}>
              {user?.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>

      {/* Statistics Grid */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="glass-card rounded-2xl p-6 dashboard-optimized">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="glass-accent rounded-xl p-3">
                  <svg className={`h-6 w-6 ${isDark ? 'text-white' : 'text-gray-800'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className={`text-mono text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Analyses</p>
                <p className={`heading-mono text-2xl font-semibold text-glow ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {dashboardData.stats?.analyses?.totalAnalyses || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-6 dashboard-optimized">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="glass-accent rounded-xl p-3">
                  <svg className={`h-6 w-6 ${isDark ? 'text-white' : 'text-gray-800'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className={`text-mono text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Client Records</p>
                <p className={`heading-mono text-2xl font-semibold text-glow ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {dashboardData.stats?.analyses?.totalClientRecords || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-6 dashboard-optimized">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="glass-accent rounded-xl p-3">
                  <svg className={`h-6 w-6 ${isDark ? 'text-white' : 'text-gray-800'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className={`text-mono text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Custom Settings</p>
                <p className={`heading-mono text-2xl font-semibold text-glow ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {dashboardData.stats?.settings?.totalCustomSettings || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Analyses */}
      {dashboardData?.recentAnalyses && dashboardData.recentAnalyses.length > 0 && (
        <div className="glass-card rounded-2xl p-8 dashboard-optimized">
          <h3 className={`heading-mono text-lg text-glow mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            Recent Analyses
          </h3>
          <div className="space-y-4">
            {dashboardData.recentAnalyses.map((analysis) => (
              <div key={analysis.id} className={`glass-dark rounded-lg p-4 ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'} transition-colors`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-mono text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {analysis.name}
                    </p>
                    <p className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                      {analysis.description || 'No description'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatDate(analysis.updatedAt)}
                    </p>
                    <p className={`text-mono text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-1`}>
                      {analysis.stats?.clientRecords} client records
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountOverview;